import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { loadStripe, type Stripe } from '@stripe/stripe-js';
import {
  Elements,
  PaymentElement,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js';
import { Button, Input, Text, EmptyState, Modal } from '@teals/ui-web';
import {
  useCreateSetupIntentMutation,
  useDetachPaymentMethodMutation,
  useGetBillingBudgetQuery,
  useGetBillingProfileQuery,
  useGetInvoicesQuery,
  useGetPaymentMethodsQuery,
  useGetPaymentReadinessQuery,
  useGetStripeConfigQuery,
  useSetDefaultPaymentMethodMutation,
  useUpdateBillingBudgetMutation,
  useUpdateBillingProfileMutation,
  type BillingProfile,
} from '../../store/api/billingApi';
import { env } from '../../utils/env';
import {
  AppBudgetRow,
  FieldGrid,
  Footnote,
  PanelCard,
  SectionTitle,
} from './Payments.styles';

const EMPTY_PROFILE: BillingProfile = {
  businessName: '',
  billingEmail: '',
  addressLine1: '',
  addressLine2: '',
  city: '',
  state: '',
  postalCode: '',
  country: '',
  taxId: '',
};

function readinessMessage(reason?: string | null): string {
  switch (reason) {
    case 'amount_due':
      return 'A payment method is required — your current period has an estimated amount due.';
    case 'subscription_active':
      return 'Add a payment method for your subscription before the next invoice.';
    case 'free_period_ending':
      return 'Your free subscription period is ending soon. Add a card to avoid interruption.';
    default:
      return 'Add a payment method to continue billing without interruption.';
  }
}

const SetupForm: React.FC<{
  onDone: () => void;
  onError: (msg: string) => void;
}> = ({ onDone, onError }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [busy, setBusy] = useState(false);

  const onSubmit = async () => {
    if (!stripe || !elements) return;
    setBusy(true);
    onError('');
    const { error } = await stripe.confirmSetup({
      elements,
      redirect: 'if_required',
      confirmParams: {
        return_url: `${window.location.origin}/payments?tab=billing`,
      },
    });
    setBusy(false);
    if (error) {
      onError(error.message || 'Card setup failed');
      return;
    }
    onDone();
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
      <PaymentElement />
      <Button variant="primary" onClick={onSubmit} disabled={!stripe || busy}>
        {busy ? 'Saving…' : 'Save card'}
      </Button>
    </div>
  );
};

export const BillingPanel: React.FC = () => {
  const { data: profile } = useGetBillingProfileQuery();
  const [updateProfile, { isLoading: savingProfile }] =
    useUpdateBillingProfileMutation();
  const { data: budget } = useGetBillingBudgetQuery();
  const [updateBudget, { isLoading: savingBudget }] =
    useUpdateBillingBudgetMutation();
  const { data: methods, refetch: refetchMethods } = useGetPaymentMethodsQuery();
  const { data: invoices } = useGetInvoicesQuery();
  const { data: readiness } = useGetPaymentReadinessQuery();
  const { data: stripeConfig } = useGetStripeConfigQuery();
  const [createSetupIntent] = useCreateSetupIntentMutation();
  const [setDefaultPm] = useSetDefaultPaymentMethodMutation();
  const [detachPm] = useDetachPaymentMethodMutation();

  const [form, setForm] = useState<BillingProfile>(EMPTY_PROFILE);
  const [tokenCap, setTokenCap] = useState('');
  const [alertPct, setAlertPct] = useState('85');
  const [modalOpen, setModalOpen] = useState(false);
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [cardError, setCardError] = useState('');
  const [savedMsg, setSavedMsg] = useState('');
  const [profileHydrated, setProfileHydrated] = useState(false);
  const [budgetHydrated, setBudgetHydrated] = useState(false);

  const publishableKey =
    stripeConfig?.publishableKey || env.STRIPE_PUBLISHABLE_KEY || '';

  const stripePromise = useMemo<Promise<Stripe | null> | null>(() => {
    if (!publishableKey) return null;
    return loadStripe(publishableKey);
  }, [publishableKey]);

  useEffect(() => {
    if (profile && !profileHydrated) {
      setForm({
        businessName: profile.businessName || '',
        billingEmail: profile.billingEmail || '',
        addressLine1: profile.addressLine1 || '',
        addressLine2: profile.addressLine2 || '',
        city: profile.city || '',
        state: profile.state || '',
        postalCode: profile.postalCode || '',
        country: profile.country || '',
        taxId: profile.taxId || '',
      });
      setProfileHydrated(true);
    }
  }, [profile, profileHydrated]);

  useEffect(() => {
    if (budget && !budgetHydrated) {
      const cap = budget.organisation?.budgetConfig?.tokenCap;
      if (cap != null) setTokenCap(String(cap));
      const alert = budget.organisation?.budgetConfig?.alertThresholdPct;
      if (alert != null) setAlertPct(String(alert));
      setBudgetHydrated(true);
    }
  }, [budget, budgetHydrated]);

  const onSaveProfile = async () => {
    setSavedMsg('');
    await updateProfile(form).unwrap();
    setSavedMsg('Billing profile saved.');
  };

  const onSaveOrgBudget = async () => {
    setSavedMsg('');
    await updateBudget({
      budgetConfig: {
        tokenCap: tokenCap ? Number(tokenCap) : undefined,
        period: 'monthly',
        alertThresholdPct: alertPct ? Number(alertPct) : undefined,
      },
    }).unwrap();
    setSavedMsg('Organisation budget saved.');
  };

  const setField =
    (key: keyof BillingProfile) =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setForm((prev) => ({ ...prev, [key]: e.target.value }));
    };

  const openAddCard = async () => {
    setCardError('');
    setClientSecret(null);
    setModalOpen(true);
    if (!publishableKey) {
      setCardError(
        'Stripe is not configured for this environment. Ask an operator to set publishable_key in lumina/{env}/stripe.',
      );
      return;
    }
    try {
      const intent = await createSetupIntent().unwrap();
      setClientSecret(intent.clientSecret);
    } catch (err: unknown) {
      const msg =
        err && typeof err === 'object' && 'data' in err
          ? JSON.stringify((err as { data: unknown }).data)
          : 'Could not start card setup';
      setCardError(String(msg));
    }
  };

  const onCardSaved = async () => {
    setModalOpen(false);
    setClientSecret(null);
    setSavedMsg('Payment method saved.');
    await refetchMethods();
  };

  const paymentMethods = methods?.paymentMethods || [];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
      {readiness?.cardRequired ? (
        <PanelCard padding={16}>
          <Text variant="body1" style={{ color: 'var(--warning, #b45309)' }}>
            {readinessMessage(readiness.reason)}
          </Text>
          <Button variant="primary" onClick={openAddCard} style={{ marginTop: 8 }}>
            Add payment method
          </Button>
        </PanelCard>
      ) : null}

      <PanelCard padding={24}>
        <SectionTitle>Billing profile</SectionTitle>
        <FieldGrid>
          <Input
            label="Legal / business name"
            value={form.businessName}
            onChange={setField('businessName')}
          />
          <Input
            label="Billing contact email"
            value={form.billingEmail}
            onChange={setField('billingEmail')}
          />
          <Input
            label="Address line 1"
            value={form.addressLine1}
            onChange={setField('addressLine1')}
          />
          <Input
            label="Address line 2"
            value={form.addressLine2}
            onChange={setField('addressLine2')}
          />
          <Input label="City" value={form.city} onChange={setField('city')} />
          <Input label="State" value={form.state} onChange={setField('state')} />
          <Input
            label="Postal code"
            value={form.postalCode}
            onChange={setField('postalCode')}
          />
          <Input
            label="Country"
            value={form.country}
            onChange={setField('country')}
          />
          <Input label="Tax ID" value={form.taxId} onChange={setField('taxId')} />
        </FieldGrid>
        <Button variant="primary" onClick={onSaveProfile} disabled={savingProfile}>
          {savingProfile ? 'Saving…' : 'Save billing profile'}
        </Button>
      </PanelCard>

      <PanelCard padding={24}>
        <SectionTitle>Payment methods</SectionTitle>
        <Footnote>
          Cards are stored with Stripe. Lumina never stores raw card numbers.
        </Footnote>
        {paymentMethods.length === 0 ? (
          <EmptyState
            title="No payment methods yet"
            description="Add a card anytime — required when an amount is due or your free period ends."
          />
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            {paymentMethods.map((pm) => (
              <AppBudgetRow key={pm.id}>
                <span>
                  {pm.brand?.toUpperCase()} •••• {pm.last4}
                  {pm.isDefault ? ' (default)' : ''}
                  {' · '}
                  {pm.expMonth}/{pm.expYear}
                </span>
                <span style={{ display: 'flex', gap: '0.5rem' }}>
                  {!pm.isDefault ? (
                    <Button
                      variant="outline"
                      onClick={() => setDefaultPm({ paymentMethodId: pm.id })}
                    >
                      Make default
                    </Button>
                  ) : null}
                  <Button
                    variant="outline"
                    onClick={() => detachPm({ paymentMethodId: pm.id })}
                  >
                    Remove
                  </Button>
                </span>
              </AppBudgetRow>
            ))}
          </div>
        )}
        <Button variant="outline" onClick={openAddCard} style={{ marginTop: 12 }}>
          Add payment method
        </Button>
      </PanelCard>

      <PanelCard padding={24}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: '1rem',
            flexWrap: 'wrap',
          }}
        >
          <SectionTitle>Budgets</SectionTitle>
          <Link to="/analytics">View utilisation</Link>
        </div>
        <Footnote>
          Set an organisation-wide monthly <strong>token</strong> cap here.
          Application caps are edited on each application’s Configuration tab.
          Staff do not set these limits.
        </Footnote>
        <FieldGrid>
          <Input
            label="Monthly org token limit"
            value={tokenCap}
            onChange={(e) => setTokenCap(e.target.value)}
            placeholder="e.g. 10000000"
          />
          <Input
            label="Alert threshold (%)"
            value={alertPct}
            onChange={(e) => setAlertPct(e.target.value)}
            placeholder="85"
          />
        </FieldGrid>
        <Button variant="primary" onClick={onSaveOrgBudget} disabled={savingBudget}>
          {savingBudget ? 'Saving…' : 'Save org budget'}
        </Button>

        <Text variant="h4" style={{ marginTop: '0.5rem' }}>
          Application budgets
        </Text>
        {(budget?.applications || []).map((app) => (
          <AppBudgetRow key={app.applicationId}>
            <span>{app.name}</span>
            <span>
              cap{' '}
              {(app.budgetConfig as { tokenCap?: number } | null)?.tokenCap ?? '—'}{' '}
              · <Link to={`/applications/${app.applicationId}`}>Edit on app →</Link>
            </span>
          </AppBudgetRow>
        ))}
        {!budget?.applications?.length && (
          <Text variant="body2">No applications yet.</Text>
        )}
      </PanelCard>

      <PanelCard padding={24}>
        <SectionTitle>Invoices</SectionTitle>
        {(invoices?.invoices?.length ?? 0) === 0 ? (
          <EmptyState
            title="No invoices yet"
            description="Monthly invoices appear here after the billing period closes."
          />
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            {invoices!.invoices.map((inv) => (
              <AppBudgetRow key={inv.id}>
                <span>
                  {inv.date} · {inv.invoiceNumber} · ${inv.amount.toFixed(2)} ·{' '}
                  {inv.status}
                </span>
                {inv.downloadUrl ? (
                  <a href={inv.downloadUrl} target="_blank" rel="noreferrer">
                    Download
                  </a>
                ) : (
                  <span />
                )}
              </AppBudgetRow>
            ))}
          </div>
        )}
      </PanelCard>

      {savedMsg ? (
        <Text variant="body2" style={{ color: 'var(--success, #15803d)' }}>
          {savedMsg}
        </Text>
      ) : null}

      <Modal
        isOpen={modalOpen}
        onClose={() => {
          setModalOpen(false);
          setClientSecret(null);
        }}
        title="Add Payment Method"
        message={cardError || 'Enter card details securely via Stripe.'}
        acceptButtonText="Close"
      />

      {modalOpen && clientSecret && stripePromise ? (
        <PanelCard padding={24}>
          <SectionTitle>Enter card details</SectionTitle>
          {cardError ? (
            <Text variant="body2" style={{ color: 'var(--error, #b91c1c)' }}>
              {cardError}
            </Text>
          ) : null}
          <Elements
            stripe={stripePromise}
            options={{ clientSecret, appearance: { theme: 'stripe' } }}
          >
            <SetupForm onDone={onCardSaved} onError={setCardError} />
          </Elements>
        </PanelCard>
      ) : null}
    </div>
  );
};

export default BillingPanel;
